import { useEffect, useId, useState, type FormEvent } from 'react'
import { ApiError } from '@/api/client'
import { useSetHandregalNachschub, type HandregalNachschubWert } from '@/api/warehouse'
import './HandregalNachschubFeld.css'

interface Props {
  merchantSku: string
  wert: HandregalNachschubWert | null
  darfAendern: boolean
}
const MENGENFEHLER = 'Bitte eine ganze Zahl von 1 bis 999 eingeben.'
const feldwert = (wert: HandregalNachschubWert | null, art: HandregalNachschubWert['art']) =>
  wert?.art === art ? String(wert.menge) : ''

export default function HandregalNachschubFeld({ merchantSku, wert, darfAendern }: Props) {
  const id = useId()
  const mutation = useSetHandregalNachschub()
  const [gespeichert, setGespeichert] = useState(wert)
  const [stueck, setStueck] = useState(() => feldwert(wert, 'stueck'))
  const [kartons, setKartons] = useState(() => feldwert(wert, 'kartons'))
  const [fehler, setFehler] = useState<string | null>(null)
  const [erfolg, setErfolg] = useState(false)
  const art = wert?.art
  const menge = wert?.menge

  useEffect(() => {
    const aktuell = art && menge !== undefined ? { art, menge } : null
    setGespeichert(aktuell)
    setStueck(feldwert(aktuell, 'stueck'))
    setKartons(feldwert(aktuell, 'kartons'))
    setFehler(null)
    setErfolg(false)
  }, [merchantSku, art, menge])

  const geaendert = stueck !== feldwert(gespeichert, 'stueck') || kartons !== feldwert(gespeichert, 'kartons')
  function aendern(value: string, art: 'stueck' | 'kartons') {
    if (art === 'stueck') setStueck(value)
    else setKartons(value)
    setFehler(null)
    setErfolg(false)
  }
  async function speichern(standard = false) {
    if (!darfAendern || mutation.isPending || (!standard && !geaendert)) return
    const s = standard ? '' : stueck
    const k = standard ? '' : kartons
    const gueltig = (text: string) => text === '' || (/^\d{1,3}$/.test(text) && Number(text) >= 1)
    if (!gueltig(s) || !gueltig(k) || (s !== '' && k !== '')) {
      setFehler(MENGENFEHLER)
      return
    }
    setFehler(null)
    setErfolg(false)
    if (standard) { setStueck(''); setKartons('') }
    try {
      const res = await mutation.mutateAsync({ merchantSku,
        menge_stueck: s === '' ? null : Number(s), menge_kartons: k === '' ? null : Number(k) })
      const aktuell = res.ist_standard ? null : { art: res.art, menge: res.menge }
      setGespeichert(aktuell)
      setStueck(feldwert(aktuell, 'stueck'))
      setKartons(feldwert(aktuell, 'kartons'))
      setErfolg(true)
    } catch (error) {
      setFehler(error instanceof ApiError && error.status === 403 ? 'Nur Leiter können den Nachschub ändern.'
        : error instanceof ApiError ? error.message : 'Nachschub konnte nicht gespeichert werden. Bitte erneut versuchen.')
    }
  }
  function submit(event: FormEvent) {
    event.preventDefault()
    void speichern()
  }
  const text = gespeichert ? `Nachschub: ${gespeichert.menge} ${gespeichert.art === 'stueck'
    ? 'Stück' : gespeichert.menge === 1 ? 'Karton' : 'Kartons'}` : 'Nachschub: 1 Karton (Standard)'

  return <div className="nachschub-feld">
    <div className="nachschub-zeile">
      <span className={gespeichert ? 'nachschub-wert' : 'nachschub-standard'}>{text}</span>
      {darfAendern && <form onSubmit={submit} noValidate aria-label="Nachschub ändern" aria-busy={mutation.isPending}>
        <label htmlFor={`${id}-stueck`}>Stück
          <input id={`${id}-stueck`} type="text" inputMode="numeric" value={stueck}
            disabled={mutation.isPending || kartons !== ''} title="Entweder Stück oder Kartons"
            aria-describedby={fehler ? `${id}-fehler` : undefined} aria-invalid={!!fehler}
            onChange={event => aendern(event.target.value, 'stueck')} />
        </label>
        <label htmlFor={`${id}-kartons`}>Kartons
          <input id={`${id}-kartons`} type="text" inputMode="numeric" value={kartons}
            disabled={mutation.isPending || stueck !== ''} title="Entweder Stück oder Kartons"
            aria-describedby={fehler ? `${id}-fehler` : undefined} aria-invalid={!!fehler}
            onChange={event => aendern(event.target.value, 'kartons')} />
        </label>
        <button type="submit" disabled={!geaendert || mutation.isPending}>Speichern</button>
        {gespeichert && <button type="button" disabled={mutation.isPending} onClick={() => void speichern(true)}>Auf Standard zurück</button>}
      </form>}
    </div>
    {fehler && <div id={`${id}-fehler`} role="alert">{fehler}</div>}
    {erfolg && <span role="status" className="nachschub-standard">Gespeichert</span>}
  </div>
}
